import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AgGridTable from '../../../components/AgGridTable';
import SidebarPanel from '../../../components/admin/SidebarPanel';
import EducationalTooltip from '../../../components/shared/EducationalTooltip';
import ModelDocumentationModal from '../../../components/shared/ModelDocumentationModal';
import MultiProfileManager from '../../../components/shared/MultiProfileManager';
import EditableUserDetails from '../../../components/admin/EditableUserDetails';
import AlertMessage from '../../../components/shared/alertMessage';
import PasswordInput from '../../../components/shared/PasswordInput';
import { adminApi } from '../../../services/api';
import { validatePassword } from '../../../utils/passwordValidation';

const PatientsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { patientId } = useParams();
  const gridRef = useRef(null);

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPanel, setShowPanel] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ health_system_id: '', is_active: 'true', search: '' });
  const [healthSystems, setHealthSystems] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [passwordData, setPasswordData] = useState({ password: '', confirmPasswordChange: false });
  const [savingPassword, setSavingPassword] = useState(false);
  const [alertState, setAlertState] = useState({ show: false, type: 'info', title: '', message: '' });
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', dob: '', gender: '', phone: '',
    street_address: '', city: '', state: '', zip_code: '', health_system_id: '', is_active: true,
    enable_portal_access: false, username: '', password: ''
  });

  useEffect(() => {
    fetchUsers();
    fetchHealthSystems();
  }, [filters]);

  useEffect(() => {
    if (location.state?.mode === 'create') {
      setIsCreateMode(true);
      setSelectedUser(null);
      setShowPanel(true);
    }
  }, [location.state]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = { user_type: 'patient', ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v)) };
      const res = await adminApi.getUsers(params);
      setUsers(res.data?.users || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const fetchHealthSystems = async () => {
    try {
      const res = await adminApi.getHealthSystems();
      setHealthSystems(res.data?.health_systems || []);
    } catch (err) {
      console.error('Failed to load health systems:', err);
    }
  };

  const handleRowClick = useCallback((event) => {
    setIsCreateMode(false);
    setSelectedUser(event.data);
    setShowPanel(true);
    setActiveTab('overview');
    navigate(`/admin/users/patients/${event.data.id}`, { replace: true });
  }, [navigate]);

  // Effect to auto-open patient details from URL parameter
  useEffect(() => {
    if (patientId && users.length > 0) {
      const user = users.find(u => u.id === patientId);
      if (user) handleRowClick({ data: user });
    }
  }, [patientId, users, handleRowClick]);

  const handleClosePanel = () => {
    setShowPanel(false);
    setSelectedUser(null);
    setIsCreateMode(false);
    setFormData({
      first_name: '', last_name: '', email: '', dob: '', gender: '', phone: '',
      street_address: '', city: '', state: '', zip_code: '', health_system_id: '', is_active: true,
      enable_portal_access: false, username: '', password: ''
    });
    setPasswordData({ password: '', confirmPasswordChange: false });
    navigate('/admin/users/patients', { replace: true });
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setAlertState({ show: false, type: 'info', title: '', message: '' }); // Clear previous alerts

    // Validate DOB is provided
    if (!formData.dob || formData.dob.trim() === '') {
      setAlertState({ show: true, type: 'error', title: 'Validation Error', message: 'Date of Birth is required' });
      return;
    }

    // Validate password if portal access is enabled
    if (formData.enable_portal_access) {
      const passwordValidation = validatePassword(formData.password);
      if (!passwordValidation.isValid) {
        setAlertState({ show: true, type: 'error', title: 'Validation Error', message: passwordValidation.error });
        return;
      }
    }

    try {
      // Convert empty strings to null for UUID fields
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        dob: formData.dob,
        gender: formData.gender,
        phone: formData.phone,
        street_address: formData.street_address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip_code,
        health_system_id: formData.health_system_id || null,
        is_active: formData.is_active,
        user_type: 'patient'
      };

      // Only include portal access credentials if enabled
      if (formData.enable_portal_access) {
        payload.username = formData.username;
        payload.email = formData.email;
        payload.password = formData.password;
      }

      await adminApi.post('/admin/users/', payload);
      fetchUsers();
      handleClosePanel();
      setAlertState({ show: true, type: 'success', title: '', message: 'Patient created successfully' });
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Failed to create patient', message: err.response?.data?.message || err.message });
    }
  };

  const handleSavePassword = async () => {
    try {
      setSavingPassword(true);
      setAlertState({ show: false, type: 'info', title: '', message: '' });

      // Validate password
      if (passwordData.password && passwordData.password.trim() !== '') {
        const passwordValidation = validatePassword(passwordData.password);
        if (!passwordValidation.isValid) {
          setAlertState({ show: true, type: 'error', title: 'Validation Error', message: passwordValidation.error });
          setSavingPassword(false);
          return;
        }

        if (!passwordData.confirmPasswordChange) {
          setAlertState({ show: true, type: 'error', title: 'Confirmation Required', message: 'Please confirm the password change by checking the confirmation box' });
          setSavingPassword(false);
          return;
        }

        // Update password
        const response = await adminApi.put(`/admin/users/patient/${selectedUser.id}/`, { password: passwordData.password });

        if (response.data?.success) {
          setAlertState({ show: true, type: 'success', title: 'Password updated successfully', message: '' });
          setPasswordData({ password: '', confirmPasswordChange: false });
        } else {
          throw new Error(response.data?.message || 'Failed to update password');
        }
      }
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Failed to update password', message: err.response?.data?.message || err.message });
    } finally {
      setSavingPassword(false);
    }
  };

  const handleExport = () => {
    gridRef.current?.api.exportDataAsCsv({ fileName: `patients-${new Date().toISOString().split('T')[0]}.csv` });
  };

  const ActionsCellRenderer = (props) => {
    const { data } = props;
    if (!data) return null;

    return (
      <div className="btn-group btn-group-sm" role="group">
        <button
          className="btn btn-outline-primary"
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick({ data });
          }}
          title="View Details"
        >
          <i className="fas fa-eye me-1"></i>
          View
        </button>
        <button
          className="btn btn-outline-danger"
          onClick={(e) => {
            e.stopPropagation();
            if (window.confirm('Are you sure you want to deactivate this patient?')) {
              // TODO: Implement deactivate functionality
              console.log('Deactivate patient:', data.id);
            }
          }}
          title="Deactivate"
        >
          <i className="fas fa-ban me-1"></i>
          Deactivate
        </button>
      </div>
    );
  };

  const columnDefs = [
    { headerName: 'Name', valueGetter: p => `${p.data?.first_name || ''} ${p.data?.last_name || ''}`, width: 150 },
    { headerName: 'Email', field: 'email', width: 200 },
    { headerName: 'DOB', valueGetter: p => p.data?.dob || '-', width: 110 },
    { headerName: 'Gender', field: 'gender', width: 90 },
    { headerName: 'Phone', field: 'phone', width: 130 },
    { headerName: 'Health System', valueGetter: p => p.data?.health_systems?.[0]?.name || '-', width: 150 },
    {
      headerName: 'Portal',
      valueGetter: p => p.data?.user ? 'Yes' : 'No',
      width: 80
    },
    {
      headerName: 'Status',
      field: 'is_active',
      width: 100,
      cellRenderer: p => (
        <span className={`badge bg-${p.data?.is_active ? 'success' : 'danger'}`}>
          {p.data?.is_active ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: ActionsCellRenderer,
      width: 180,
      sortable: false,
      filter: false,
      suppressMenu: true
    }
  ];

  return (
    <>
      {error && (
        <div className="alert alert-danger">{error}</div>
      )}
      <div className="page-title-box d-flex align-items-center justify-content-between mb-3">
        <div className="col-8">
          <h4 className="text-lg">
            Patients
          </h4>
          <p className="text-muted">Patients receiving care. Each can have multiple EHR/SSO profiles per health system</p>
        </div>
        <div className="col-4 d-flex justify-content-end">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb m-0">
              <li className="breadcrumb-item"><a href="/admin">Admin</a></li>
              <li className="breadcrumb-item"><a href="">User Management</a></li>
              <li className="breadcrumb-item active">Patients</li>
            </ol>
          </nav>
        </div>
      </div>
      <div className="d-flex justify-content-end align-items-center gap-2 mb-3">
        <button className="btn btn-primary" onClick={() => navigate('/admin/users/patients', { state: { mode: 'create' } })}>
          <i className="fas fa-plus me-1" />Add Patient
        </button>
      </div>
      <div className="card">
        <div className="d-flex justify-content-between mb-3 p-2">
          <div className='d-flex gap-2'>
            <select className="form-select form-select-sm" value={filters.health_system_id} onChange={e => setFilters({...filters, health_system_id: e.target.value})}>
              <option value="">All Health Systems</option>
              {healthSystems.map(hs => <option key={hs.id || hs.health_system_id} value={hs.id}>{hs.name}</option>)}
            </select>
            <select className="form-select form-select-sm" value={filters.is_active} onChange={e => setFilters({...filters, is_active: e.target.value})}>
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
            <input type="text" className="form-control form-control-sm" placeholder="Search..." value={filters.search} onChange={e => setFilters({...filters, search: e.target.value})} />
            <vr></vr>
            <small className="text-muted text-nowrap align-self-center">
              Showing {users.length} patient{users.length !== 1 ? 's' : ''}
            </small>
          </div>
          <div className='d-flex gap-2 justify-content-end'>
            <button className="btn btn-sm btn-info" onClick={() => setShowDocsModal(true)}>
              <i className="fas fa-book me-1" />Docs
            </button>
            <button className="btn btn-sm btn-success" onClick={handleExport}>
              <i className="fas fa-file-export me-1" />Export
            </button>
          </div>
        </div>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <AgGridTable ref={gridRef} rowData={users} columnDefs={columnDefs} onRowClicked={handleRowClick} />
        )}
      </div>

      <SidebarPanel isOpen={showPanel} onClose={handleClosePanel} title={isCreateMode ? "Create Patient" : "Patient Details"}>
        {isCreateMode ? (
          <div className="card">
            <div className="card-body p-3">
              {/* Alert Component */}
              {alertState.show && (
                <AlertMessage
                  type={alertState.type}
                  title={alertState.title}
                  message={alertState.message}
                  dismissible={true}
                  onDismiss={() => setAlertState({ ...alertState, show: false })}
                  className="mb-3"
                />
              )}

              <div className="row mb-3">
                <div className="col-6">
                  <label className="form-label small mb-1">First Name *</label>
                  <input type="text" className="form-control form-control-sm" required value={formData.first_name} onChange={e => setFormData({...formData, first_name: e.target.value})} />
                </div>
                <div className="col-6">
                  <label className="form-label small mb-1">Last Name *</label>
                  <input type="text" className="form-control form-control-sm" required value={formData.last_name} onChange={e => setFormData({...formData, last_name: e.target.value})} />
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-6">
                  <label className="form-label small mb-1">Email</label>
                  <input type="email" className="form-control form-control-sm" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                </div>
                <div className="col-6">
                  <label className="form-label small mb-1">Phone</label>
                  <input type="tel" className="form-control form-control-sm" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-6">
                  <label className="form-label small mb-1">Date of Birth *</label>
                  <input type="date" className="form-control form-control-sm" required value={formData.dob} onChange={e => setFormData({...formData, dob: e.target.value})} />
                </div>
                <div className="col-6">
                  <label className="form-label small mb-1">Gender</label>
                  <select className="form-select form-select-sm" value={formData.gender} onChange={e => setFormData({...formData, gender: e.target.value})}>
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-12">
                  <label className="form-label small mb-1">Street Address</label>
                  <input type="text" className="form-control form-control-sm" value={formData.street_address} onChange={e => setFormData({...formData, street_address: e.target.value})} />
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-4">
                  <label className="form-label small mb-1">City</label>
                  <input type="text" className="form-control form-control-sm" value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} />
                </div>
                <div className="col-4">
                  <label className="form-label small mb-1">State</label>
                  <input type="text" className="form-control form-control-sm" value={formData.state} onChange={e => setFormData({...formData, state: e.target.value})} />
                </div>
                <div className="col-4">
                  <label className="form-label small mb-1">Zip Code</label>
                  <input type="text" className="form-control form-control-sm" value={formData.zip_code} onChange={e => setFormData({...formData, zip_code: e.target.value})} />
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-6">
                  <label className="form-label small mb-1">Health System</label>
                  <select className="form-select form-select-sm" value={formData.health_system_id} onChange={e => setFormData({...formData, health_system_id: e.target.value})}>
                    <option value="">Select Health System</option>
                    {healthSystems.map(hs => <option key={hs.id || hs.health_system_id} value={hs.id}>{hs.name}</option>)}
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label small mb-1">Status</label>
                  <select className="form-select form-select-sm" value={formData.is_active} onChange={e => setFormData({...formData, is_active: e.target.value === 'true'})}>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-12">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="enablePortalAccess"
                      checked={formData.enable_portal_access}
                      onChange={e => setFormData({...formData, enable_portal_access: e.target.checked, username: '', email: '', password: ''})}
                    />
                    <label className="form-check-label small" htmlFor="enablePortalAccess">
                      Enable Portal Access
                      <EducationalTooltip title="Allow patient to log in to patient portal with username and password" />
                    </label>
                  </div>
                </div>
              </div>
              {formData.enable_portal_access && (
                <>
                  <div className="row mb-3">
                    <div className="col-6">
                      <label className="form-label small mb-1">Username *</label>
                      <input type="text" className="form-control form-control-sm" required value={formData.username} onChange={e => setFormData({...formData, username: e.target.value})} />
                    </div>
                    <div className="col-6">
                      <PasswordInput
                        value={formData.password}
                        onChange={e => setFormData({...formData, password: e.target.value})}
                        required
                        label="Password"
                        showRequirements
                        className=""
                      />
                    </div>
                  </div>
                </>
              )}
              <div className="row mt-3">
                <div className="col-12">
                  <button type="button" className="btn btn-primary btn-sm me-2" onClick={handleCreateUser}>Create Patient</button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={handleClosePanel}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        ) : selectedUser && (
          <>
            <ul className="nav nav-tabs nav-tabs-custom mb-2">
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>Overview</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'ehr' ? 'active' : ''}`} onClick={() => setActiveTab('ehr')}>EHR Profiles</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'sso' ? 'active' : ''}`} onClick={() => setActiveTab('sso')}>SSO Configs</button>
              </li>
            </ul>

            {activeTab === 'overview' && (
              <>
              <div className="card">
                <div className="card-body p-3">
                  {/* Alert Component */}
                  {alertState.show && (
                    <AlertMessage
                      type={alertState.type}
                      title={alertState.title}
                      message={alertState.message}
                      dismissible={true}
                      onDismiss={() => setAlertState({ ...alertState, show: false })}
                      className="mb-3"
                    />
                  )}

                  <EditableUserDetails
                    userData={selectedUser}
                    userId={selectedUser.id}
                    userType="patient"
                    fieldConfig={[
                      { name: 'first_name', label: 'First Name', type: 'text', editable: true },
                      { name: 'last_name', label: 'Last Name', type: 'text', editable: true },
                      { name: 'email', label: 'Email', type: 'email', editable: true },
                      { name: 'phone', label: 'Phone', type: 'text', editable: true },
                      { name: 'dob', label: 'Date of Birth', type: 'date', editable: true },
                      { name: 'gender', label: 'Gender', type: 'select', editable: true, options: [
                        { value: '', label: 'Select Gender' },
                        { value: 'Male', label: 'Male' },
                        { value: 'Female', label: 'Female' },
                        { value: 'Other', label: 'Other' }
                      ]},
                      { name: 'street_address', label: 'Street Address', type: 'text', editable: true },
                      { name: 'city', label: 'City', type: 'text', editable: true },
                      { name: 'state', label: 'State', type: 'text', editable: true },
                      { name: 'zip_code', label: 'Zip Code', type: 'text', editable: true },
                      { name: 'is_active', label: 'Active', type: 'checkbox', editable: true },
                      { name: 'created_at', label: 'Created At', type: 'date', editable: false },
                    ]}
                    onSave={() => {
                      fetchUsers();
                      setAlertState({ show: true, type: 'success', title: 'Patient updated successfully', message: '' });
                    }}
                    onError={(err) => {
                      console.error('Error updating patient:', err);
                    }}
                  />
                </div>
              </div>

              {/* Password Management - Only show if patient has portal access (username exists) */}
              {selectedUser.username && (
                <div className="card mt-3">
                  <div className="card-body p-3">
                    <h6 className="mb-3">Password Management</h6>
                    <div className="row">
                      <div className="col-12">
                        <PasswordInput
                          value={passwordData.password}
                          onChange={e => { setPasswordData({...passwordData, password: e.target.value, confirmPasswordChange: false}); }}
                          required={false}
                          label="New Password"
                          placeholder="Leave blank to keep current password"
                          showRequirements
                          className="mb-2"
                        />
                        {passwordData.password && passwordData.password.trim() !== '' && (
                          <div className="form-check mt-2 mb-3">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="confirmPasswordChangePatient"
                              checked={passwordData.confirmPasswordChange}
                              onChange={e => setPasswordData({...passwordData, confirmPasswordChange: e.target.checked})}
                            />
                            <label className="form-check-label small text-muted" htmlFor="confirmPasswordChangePatient">
                              I want to update the patient&apos;s portal password. This will not affect their existing sessions.
                            </label>
                          </div>
                        )}
                        {passwordData.password && passwordData.password.trim() !== '' && (
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={handleSavePassword}
                            disabled={savingPassword}
                          >
                            {savingPassword ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1"></span>
                                Updating Password...
                              </>
                            ) : (
                              'Update Password'
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              </>
            )}

            {activeTab === 'ehr' && (
              <MultiProfileManager
                profiles={selectedUser.ehr_profiles || []}
                profileType="ehr"
                distinguishingField="ehr_system_name"
                readOnly
              />
            )}

            {activeTab === 'sso' && (
              <MultiProfileManager
                profiles={selectedUser.sso_configs || []}
                profileType="sso"
                distinguishingField="sso_provider_type"
                readOnly
              />
            )}
          </>
        )}
      </SidebarPanel>

      <ModelDocumentationModal show={showDocsModal} onHide={() => setShowDocsModal(false)} modelType="patient" />
    </>
  );
};

export default PatientsPage;
